import { getHierarchyNodes } from 'core/API/hierarchy';
import { paginationService } from 'core/services/paginationService';
import { parseUserIdFromRef } from 'core/utils/checklistEventAssignment';
import { filterSelectableWorkplaceUsers } from 'core/utils/filterVisibleWorkplaceUsers';
import { getCurrentUserHierarchyId } from 'module/onboarding/utils/hierarchyPermissions';
import { WorkplaceUserListScopeOptions } from 'module/settings/settingsGameMasterTabs/GeneralSetting/UserSettings/filterUserSettingsUsers';

export interface EnrichWorkplaceUsersOptions extends WorkplaceUserListScopeOptions {
	/** When false, skips workplace hierarchy scoping (for User Settings fetch). Default true. */
	applyWorkplaceScopeFilter?: boolean;
}

const toNumericUserId = (user: any): number | null => parseUserIdFromRef(user);

/**
 * Merges workplace_employees with /users so above-workplace users returned by /users
 * are available to assignment pickers (same as User Settings with the toggle on).
 */
export const mergeWorkplaceAndFullUsers = (
	wpUsers: any[] = [],
	fullUsers: any[] = []
): any[] => {
	const fullUsersMap = new Map<number, any>();
	for (const user of fullUsers) {
		const uid = toNumericUserId(user);
		if (uid != null) fullUsersMap.set(uid, user);
	}

	const mergedById = new Map<number, any>();
	for (const wpUser of wpUsers) {
		const uid = toNumericUserId(wpUser);
		if (uid == null) continue;
		const full = fullUsersMap.get(uid);
		mergedById.set(uid, full ? { ...full, ...wpUser, _full: full } : wpUser);
	}

	for (const full of fullUsers) {
		const uid = toNumericUserId(full);
		if (uid == null || mergedById.has(uid)) continue;
		mergedById.set(uid, full);
	}

	return [...mergedById.values()];
};

/**
 * Fetches workplace employees from /workplace_employees/users and enriches them
 * with full user data from /users. Returns an array of enriched user objects
 * sorted by lastName ascending.
 *
 * @param workplaceId - workplace identifier (string or number)
 * @returns Promise<any[]> enriched user list sorted by lastName ASC
 */
export const getEnrichedWorkplaceUsers = async (
	workplaceId: string | number,
	options: EnrichWorkplaceUsersOptions = {}
): Promise<any[]> => {
	const {
		applyWorkplaceScopeFilter = true,
		includeAboveWorkplaceUsers = false,
		currentUserHierarchyId: providedHierarchyId,
	} = options;
	const [wpData, fullData] = await Promise.all([
		paginationService.getWithHandler(
			`workplace_employees/users?workplace=${workplaceId}`,
			undefined,
			100
		),
		paginationService.getWithHandler(
			`users?workplace=${workplaceId}`,
			undefined,
			100
		),
	]);
	const wpUsers: any[] = wpData?.member || wpData?.['hydra:member'] || [];
	const fullUsers: any[] = fullData?.member || fullData?.['hydra:member'] || [];
	const enriched = mergeWorkplaceAndFullUsers(wpUsers, fullUsers);

	// 5. Apply shared visibility + workplace scope filters for dropdown pickers
	const hierarchyNodes =
		applyWorkplaceScopeFilter ? await getHierarchyNodes({ page: 1, itemsPerPage: 100 }) : [];

	const currentUserHierarchyId =
		includeAboveWorkplaceUsers
			? (providedHierarchyId ?? getCurrentUserHierarchyId())
			: null;

	const visible = filterSelectableWorkplaceUsers(enriched, {
		workplaceId: Number(workplaceId),
		hierarchyNodes,
		applyWorkplaceScopeFilter,
		includeAboveWorkplaceUsers,
		currentUserHierarchyId,
	});

	// 6. Sort by lastName ascending
	visible.sort((a: any, b: any) =>
		(a.lastName || '').localeCompare(b.lastName || '')
	);

	return visible;
};

/** Options for Schedule, To-Do, and Checklist user pickers (User Settings with above-workplace toggle on). */
export const assignmentWorkplaceUserOptions: EnrichWorkplaceUsersOptions = {
	includeAboveWorkplaceUsers: true,
};

/** Alias for getEnrichedWorkplaceUsers — canonical entry point for dropdown user lists. */
export const getSelectableWorkplaceUsers = getEnrichedWorkplaceUsers;
